import { clone } from "lodash-es";
import { merge } from "../core/Merge.js";
import { Signal } from "../core/Signal.js";
import { Calc } from "../math/Calc.js";
import { Resize } from "./Resize.js";
import { viewportSize } from "./ViewportSize.js";
import { event } from "../core/EventUtil.js";

export type ViewTimelineRangeName = "contain" | "cover" | "entry-crossing" | "entry" | "exit-crossing" | "exit";

export interface ViewTimelineRange {
    rangeName?: ViewTimelineRangeName;
    offset?: number;
}

export type ViewTimelineOffset = number | "start" | "center" | "end" | "top" | "bottom" | "right" | "left" | "middle";

export type ViewTimelineRangeValue = ViewTimelineRange | [ViewTimelineOffset, ViewTimelineOffset];

export interface ViewTimelineOptions {
    target: HTMLElement;
    container?: HTMLElement | Document;
    containerBounds?: HTMLElement | Document;
    rangeStart?: ViewTimelineRangeValue;
    rangeEnd?: ViewTimelineRangeValue;
    onUpdate?: (timeline: ViewTimeline) => void;
}

/**
 * @see https://scroll-driven-animations.style/tools/view-timeline/ranges
 */
export class ViewTimeline {

    public onUpdate = Signal.create<[ViewTimeline]>();

    private static _dirty: ViewTimeline[] = [];

    private _target: HTMLElement;
    private _container: HTMLElement | Document;
    private _rangeStart: ViewTimelineRangeValue;
    private _rangeEnd: ViewTimelineRangeValue;
    private _ratio: number = 0;
    private _disposables: DisposableObject[] = [];
    private _isDirtyTriggerUpdate: boolean = false;
    private _batchUpdates: boolean = true;

    constructor(options: ViewTimelineOptions) {
        options = merge({
            container: options.target.ownerDocument,
            rangeStart: { rangeName: "contain", offset: 0 },
            rangeEnd: { rangeName: "contain", offset: 100 },
        }, options);

        this._target = options.target;
        this._container = options.container;
        this.setRangeStart(options.rangeStart);
        this.setRangeEnd(options.rangeEnd);

        if (typeof options.onUpdate === "function") {
            this.onUpdate.add(options.onUpdate);
        }

        this._disposables.push(
            this.onUpdate,
            event.on({
                type: "scroll",
                passive: true,
                scope: this._container,
                listener: () => {
                    this.update();
                },
            })
        );

        const resizer = Resize.create(this._target,
            this.isDocument(this._container)
                ? this._container.body
                : this._container as HTMLElement);
        this._disposables.push(resizer);
        resizer.add(() => { this.update() });

        this.updateInternal(false);
        this.onUpdate.dispatch(this);
    }

    /**
     * Good explanation of the ranges (partially wrong: entry-crossing != entry and exti != exit-crossing): 
     * https://dev.to/link2twenty/future-of-css-scroll-animations-52ia
     */
    private getTop(value: ViewTimelineRangeValue, scrollport: DOMRect, principal: DOMRect): number {


        if (Array.isArray(value)) {
            /**
             * @see https://www.framer.com/motion/use-scroll/#scroll-offsets
             */
            const pT = typeof value[0] === "number" ? value[0] * principal.height : Calc.relation(value[0], principal.height, 0);
            const sT = typeof value[1] === "number" ? value[1] * scrollport.height : Calc.relation(value[1], scrollport.height, 0);
            const scrollportY = scrollport.top + sT;
            return scrollportY - pT;
        } else {
            const rangeName = value.rangeName;
            const offset = value.offset;

            let start = 0;
            let end = 0;

            /**
             * @see https://drafts.csswg.org/scroll-animations/#view-timelines-ranges
             */
            if (rangeName === "contain") {
                start = Math.max(scrollport.bottom - principal.height, scrollport.top);
                end = Math.min(scrollport.bottom - principal.height, scrollport.top);
            } else if (rangeName === "cover") {
                start = scrollport.bottom;
                end = scrollport.top - principal.height;
            } else if (rangeName === "entry") {
                start = scrollport.bottom;
                end = Math.max(scrollport.bottom - principal.height, scrollport.top);
            } else if (rangeName === "entry-crossing") {
                start = scrollport.bottom;
                end = scrollport.bottom - principal.height;
            } else if (rangeName === "exit") {
                start = Math.min(scrollport.bottom - principal.height, scrollport.top);
                end = scrollport.top - principal.height;
            } else if (rangeName === "exit-crossing") {
                start = scrollport.top;
                end = scrollport.top - principal.height;
            } else {
                console.warn(`Unknown range name "${rangeName}".`);
                return 0;
            }

            if (start < end) {
                const temp = start;
                start = end;
                end = temp;
            }
            return Calc.interpolate(offset / 100, start, end);
        }

        return 0;
    }

    /**
     * @see https://andreiglingeanu.me/nulify-transforms/
     */
    private nullifyTransform(transform: string, bounds: DOMRect): DOMRect {
        // 1
        const { x, y, width, height } = bounds;
        const transformArr = transform.split(/\(|,|\)/)
            .slice(1, -1)
            .map((v) => parseFloat(v));

        if (transformArr.length == 6) {
            // 2D matrix
            const t = transformArr;

            // 2
            const det = t[0] * t[3] - t[1] * t[2];

            // 3
            return new DOMRect(
                (x * t[3] - y * t[2] + t[2] * t[5] - t[4] * t[3]) / det,
                (-x * t[1] + y * t[0] + t[4] * t[1] - t[0] * t[5]) / det,
                width / t[0],
                height / t[3]
            );
        } else {
            // This case is not handled because it's very rarely needed anyway.
            // We just return the tranformed metrics, as they are, for consistency.

            //TODO: 3d matrix handling
        }

        return new DOMRect(x, y, width, height);
    }

    private isDocument(element: HTMLElement | Document): element is Document {
        return element instanceof Document || (this._target.ownerDocument as any) === element;
    }

    public update(): void {
        this.updateInternal(true);
    }

    private updateInternal(trigger: boolean): void {

        let boundsViewport: DOMRect;
        if (this.isDocument(this._container)) {
            boundsViewport = new DOMRect(0, 0, viewportSize.lvw, viewportSize.lvh);
            // boundsViewport = new DOMRect(0, 0, window.innerWidth, window.innerHeight);
        } else {
            boundsViewport = this._container.getBoundingClientRect();
        }

        let boundsElement: DOMRect;

        // transform: scale(0.5);

        // let dummy = document.querySelector<HTMLElement>(".dummy-element");
        // if(!dummy){
        //     dummy = document.createElement("div");
        //     dummy.classList.add("dummy-element");
        //     dummy.style.position = "fixed";
        //     dummy.style.top = "0";
        //     dummy.style.left = "0";
        //     dummy.style.backgroundColor = "black";
        //     dummy.style.color = "white";
        //     document.body.appendChild(dummy);
        // }


        /**
         * CSS Spec: "Transforms are ignored, but relative and absolute positioning are accounted for."
         * So, we have to nullify/revert the transform on the bounding box.
         * @see https://www.w3.org/TR/scroll-animations-1/#view-timelines-ranges
         */
        const style = window.getComputedStyle(this._target);
        if (style.transform !== "none") {
            boundsElement = new DOMRect(
                Calc.parse(style.left, 0),
                Calc.parse(style.top, 0),
                this._target.offsetWidth,
                this._target.offsetHeight
            );




            if (this._target.parentElement) {
                const parentBounds = this._target.parentElement.getBoundingClientRect();
                boundsElement.x += parentBounds.x;
                boundsElement.y += parentBounds.y;
            }
        } else {
            boundsElement = this._target.getBoundingClientRect();
        }

        let start = this.getTop(this._rangeStart || { rangeName: "contain", offset: 0 }, boundsViewport, boundsElement);
        let end = this.getTop(this._rangeEnd || { rangeName: "contain", offset: 100 }, boundsViewport, boundsElement);

        // if (start < end) {
        //     const temp = start;
        //     start = end;
        //     end = temp;
        // }

        let ratio = Calc.ratio(boundsElement.y, start, end);
        ratio = Calc.clamp(ratio, 0, 1);

        // dummy.textContent = `x: ${boundsElement.x.toFixed(1)}, y: ${boundsElement.y.toFixed(1)}, width: ${boundsElement.width}, height: ${boundsElement.height}, ratio: ${ratio.toFixed(2)}`;

        if (ratio <= 0.001) {
            ratio = 0;
        } else if (ratio >= 0.999) {
            ratio = 1;
        }

        // console.log("start", start, "end", end);
        // console.log("ratio", ratio);
        // console.log("scrollTop", document.documentElement.scrollTop);

        if (this._ratio !== ratio) {
            this._ratio = ratio;

            if (trigger) {
                if (this._batchUpdates) {
                    this.setDirtyTriggerUpdate();
                } else {
                    this.onUpdate.dispatch(this);
                }
            }
        }
    }

    /**
     * Batch update trigger to avoid reflows.
     * 
     * We calling element.getBoundingClientRect() in the update method!
     */
    private setDirtyTriggerUpdate(): void {
        if (this._isDirtyTriggerUpdate) {
            return;
        }
        this._isDirtyTriggerUpdate = true;
        ViewTimeline._dirty.push(this);

        if (ViewTimeline._dirty.length === 1) {
            // queueMicrotask(() => {
            requestAnimationFrame(() => {
                for (let i = 0; i < ViewTimeline._dirty.length; i++) {
                    ViewTimeline._dirty[i].onUpdate.dispatch(ViewTimeline._dirty[i]);
                    ViewTimeline._dirty[i]._isDirtyTriggerUpdate = false;
                }
                ViewTimeline._dirty = [];
            });
        }
    }

    public get ratio(): number {
        return this._ratio;
    }

    public get rangeStart(): ViewTimelineRange {
        if (!this._rangeStart) {
            return null;
        }
        if (Array.isArray(this._rangeStart)) {
            return this._rangeStart.slice() as any;
        }
        return clone(this._rangeStart);
    }

    public set rangeStart(value: ViewTimelineRange) {
        this.setRangeStart(value);
        this.update();
    }

    private setRangeStart(value: ViewTimelineRangeValue): void {
        if (!value) {
            this._rangeStart = null;
        } else if (Array.isArray(value)) {
            this._rangeStart = value.slice() as any;
        } else {
            this._rangeStart = merge({ rangeName: "contain", offset: 0 }, value);
        }
    }

    public get rangeEnd(): ViewTimelineRangeValue {
        if (!this._rangeEnd) {
            return null;
        }
        if (Array.isArray(this._rangeEnd)) {
            return this._rangeEnd.slice() as any;
        }
        return clone(this._rangeEnd);
    }

    public set rangeEnd(value: ViewTimelineRangeValue) {
        this.setRangeEnd(value);
        this.update();
    }

    private setRangeEnd(value: ViewTimelineRangeValue): void {
        if (!value) {
            this._rangeEnd = null;
        } else if (Array.isArray(value)) {
            this._rangeEnd = value.slice() as any;
        } else {
            this._rangeEnd = merge({ rangeName: "contain", offset: 100 }, value);
        }
    }

    public dispose(): void {
        this._disposables.forEach((disposable) => {
            disposable.dispose();
        });
        this.onUpdate = null;
        this._disposables = null;
        this._target = null;
        this._container = null;
        this._rangeStart = null;
        this._rangeEnd = null;
        const i = ViewTimeline._dirty.indexOf(this);
        if (i !== -1) {
            ViewTimeline._dirty.splice(i, 1);
        }
    }
}